import { useRef, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, Upload, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";

const NAMES = { companies: "Aziende", users: "Utenti", contacts: "Clienti", quotes: "Preventivi", invoices: "Fatture", appointments: "Appuntamenti",
  expenses: "Spese", prices: "Listino", ddt: "DDT", cantieri: "Cantieri", operai: "Operai", pagamenti: "Pagamenti", settings: "Impostazioni" };

export const Summary = ({ rep }) => {
  const rows = Object.entries(rep.collezioni).filter(([, s]) => s.nuovi || s.aggiornati);
  return (
    <div className="rounded-lg bg-secondary/60 p-4 space-y-3 text-sm" data-testid="archive-summary">
      <div className="font-semibold">{rep.dry_run ? "Anteprima: ecco cosa succederà" : "Fatto! Dati uniti"}</div>
      <ul className="space-y-0.5">{rep.aziende.map((a) => <li key={a.nome}>• <b>{a.nome}</b>: {a.azione}</li>)}</ul>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {rows.map(([k, s]) => (
          <div key={k} className="rounded-md bg-background px-2.5 py-1.5" data-testid={`archive-row-${k}`}>
            <div className="text-xs text-muted-foreground">{NAMES[k] || k}</div>
            <div className="font-medium">+{s.nuovi} nuovi{s.aggiornati ? ` · ${s.aggiornati} aggiornati` : ""}</div>
          </div>
        ))}
        {!rows.length && <div className="text-muted-foreground">Nessun dato nuovo: è già tutto presente.</div>}
      </div>
      {rep.doppioni.length > 0 && (
        <details className="text-xs"><summary className="cursor-pointer">{rep.doppioni.length} dati presenti in entrambe: tengo il più recente</summary>
          <ul className="mt-1 space-y-0.5">{rep.doppioni.slice(0, 30).map((d, i) => <li key={i}>{d.dato} → versione {d.tenuta === "prova" ? "di prova" : "pubblicata"}</li>)}</ul>
        </details>
      )}
      {rep.avvisi.map((a) => <div key={a} className="flex gap-1.5 text-amber-700 text-xs"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{a}</div>)}
    </div>
  );
};

// Super Admin: scarica l'archivio di questa app o unisci quello di un'altra (es. prova → pubblicata).
export const ArchiveCard = () => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState("");

  const download = async () => {
    setBusy("export");
    try {
      const { data, headers } = await api.get("/superadmin/archive/export", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "kgestionale-archivio.json";
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Download non riuscito, riprova"); }
    setBusy("");
  };

  const send = async (f, dry) => {
    setBusy(dry ? "check" : "merge");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/superadmin/archive/import?dry_run=${dry}`, fd);
      setRep(data);
      if (!dry) { toast.success("Archivio unito"); setFile(null); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setFile(f); setRep(null); send(f, true); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="archive-card">
      <div>
        <h3 className="font-heading font-bold flex items-center gap-2"><Archive className="w-4 h-4 text-primary" /> Archivio dati</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Scarica tutti i dati delle ditte vere (le ditte di test restano fuori) oppure carica l'archivio di un'altra app per unirlo a questa. Non viene cancellato niente: a parità di dato tengo il più recente.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={download} disabled={!!busy} data-testid="archive-export-button">
          {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Scarica archivio
        </Button>
        <Button variant="outline" onClick={() => input.current?.click()} disabled={!!busy} data-testid="archive-upload-button">
          {busy === "check" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica archivio da unire
        </Button>
        <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={pick} data-testid="archive-file-input" />
      </div>
      {rep && <Summary rep={rep} />}
      {rep?.dry_run && file && Object.values(rep.collezioni).some((s) => s.nuovi || s.aggiornati) && (
        <Button onClick={() => send(file, false)} disabled={!!busy} data-testid="archive-merge-button">
          {busy === "merge" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Unisci adesso
        </Button>
      )}
    </div>
  );
};

export default ArchiveCard;
